import * as fs from 'fs';
import * as path from 'path';
import { fileURLToPath } from 'url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/**
 * 配置模板变量
 */
interface TemplateVariables {
  PROJECT_NAME: string;
  PROJECT_PATH: string;
  GENERATE_TIME: string;
  AGENT_COUNT: string;
  AGENTS_LIST: string;
  CUSTOM_CONTENT?: string;
}

/**
 * 配置模板加载器
 * 
 * 设计原则：
 * 1. 模板与代码分离 - 所有硬编码文本移至模板文件
 * 2. 最小化输出 - 生成的配置文件只包含必要信息
 * 3. 动态加载 - 规范内容由 MCP 工具实时加载，不嵌入配置文件
 */
export class ConfigTemplateLoader {
  private templatesPath: string;
  
  constructor() {
    // 模板目录路径
    // 开发环境: mcp-server/build/core -> copilot-prompts/templates/config-templates
    // npm 包环境: node_modules/mta-mcp/build/core -> node_modules/mta-mcp/templates/config-templates
    const devPath = path.resolve(__dirname, '../../../templates/config-templates');
    const npmPath = path.resolve(__dirname, '../../templates/config-templates');
    
    this.templatesPath = fs.existsSync(npmPath) ? npmPath : devPath;
  }
  
  /**
   * 加载模板文件
   */
  private loadTemplate(templateName: string): string {
    const filePath = path.join(this.templatesPath, templateName);
    
    if (!fs.existsSync(filePath)) {
      throw new Error(`模板文件不存在: ${templateName}`);
    }
    
    return fs.readFileSync(filePath, 'utf-8');
  }
  
  /**
   * 替换模板变量
   */
  private replaceVariables(template: string, variables: TemplateVariables): string {
    let result = template;
    
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{{${key}}}`;
      result = result.replace(new RegExp(placeholder, 'g'), value || '');
    }
    
    return result;
  }
  
  /**
   * 生成精简版配置文件内容
   * 
   * v2.0.0 优化：
   * - 从 ~150 行精简到 ~50 行
   * - 移除重复的规则说明（由 MCP 工具动态加载）
   * - 只保留作用域声明和 Agent 引用
   */
  generateMinimalConfig(options: {
    projectName: string;
    projectPath: string;
    agents: Array<{ id: string; title: string; description?: string; path?: string }>;
    existingCustomContent?: string;
  }): string {
    const { projectName, projectPath, agents, existingCustomContent } = options;
    
    // 构建 Agent 列表
    const agentsList = agents.map(agent => `
### ${agent.title}

- **Agent ID**: \`${agent.id}\`
- **描述**: ${agent.description || '暂无描述'}
- **来源**: \`${agent.path || `npm:mta-mcp/agents/${agent.id}.agent.md`}\`
`).join('\n');
    
    // 准备模板变量
    const variables: TemplateVariables = {
      PROJECT_NAME: projectName,
      PROJECT_PATH: projectPath,
      GENERATE_TIME: new Date().toLocaleDateString('zh-CN'),
      AGENT_COUNT: String(agents.length),
      AGENTS_LIST: agentsList,
      CUSTOM_CONTENT: existingCustomContent
    };
    
    // 加载并组装模板
    let content = '';
    
    try {
      // 1. 头部
      content += this.replaceVariables(this.loadTemplate('header.md'), variables);
      content += '\n';
      
      // 2. 工作流说明（精简版）
      content += this.replaceVariables(this.loadTemplate('workflow-minimal.md'), variables);
      content += '\n';
      
      // 3. Agent 列表
      content += this.replaceVariables(this.loadTemplate('agents-section.md'), variables);
      content += '\n';
      
      // 4. 自定义内容区域
      if (existingCustomContent) {
        content += `## 📝 自定义规范\n\n`;
        content += existingCustomContent;
        content += '\n';
      } else {
        content += this.loadTemplate('custom-section.md');
      }
      
    } catch (error) {
      // 如果模板加载失败，使用内联生成
      console.error('模板加载失败，使用内联生成:', error);
      content = this.generateInlineConfig(variables);
    }
    
    return content;
  }
  
  /**
   * 内联生成配置（模板加载失败时的备选方案）
   */
  private generateInlineConfig(variables: TemplateVariables): string {
    return `<!-- ⚠️ 此文件由 MTA MCP Server 生成 -->
<!-- 🎯 作用域：此配置仅适用于当前项目 -->
<!-- 项目名称: ${variables.PROJECT_NAME} -->
<!-- 项目路径: ${variables.PROJECT_PATH} -->

# 项目开发规范 - Copilot 指令

## 🎯 作用域限制

**⚠️ 此配置仅在以下情况生效：**

1. 当前编辑的文件路径包含: \`/${variables.PROJECT_NAME}/\`
2. 或当前工作目录为: \`${variables.PROJECT_PATH}\`

**如果你在其他项目工作，请完全忽略此配置文件中的所有规范和指令。**

---

> 📌 **自动配置信息**
> - 生成时间: ${variables.GENERATE_TIME}
> - 匹配的 Agents: ${variables.AGENT_COUNT} 个

---

## ⚠️ 规范加载说明

**本项目使用 MCP 工具动态加载规范，无需在此文件中重复编写规则。**

在编写代码前，Copilot 会自动通过 MCP 工具加载规范：

| 场景 | MCP 工具调用 |
|------|-------------|
| Vue 文件 | \`get_compact_standards({ currentFile: "xxx.vue" })\` |
| 问题诊断 | \`troubleshoot({ problem: "问题描述" })\` |

---

## 📚 配置的 Agents

${variables.AGENTS_LIST}

---

## 📝 自定义规范

<!-- CUSTOM_START -->
<!-- 在此添加项目特有的规范 -->
<!-- CUSTOM_END -->
`;
  }
  
  /**
   * 检查模板目录是否存在
   */
  isTemplateDirectoryAvailable(): boolean {
    return fs.existsSync(this.templatesPath);
  }
  
  /**
   * 获取模板目录路径
   */
  getTemplatesPath(): string {
    return this.templatesPath;
  }
}
